const RESIZE_OBSERVER_DEBOUNCE_MS = 100
export const RESIZE_SETTLE_MS = 150

let resizeSettleTimer = null
let resizeSettleListeners = new Set()
let resizeListenerAttached = false

const notifyResizeSettled = () => {
    resizeSettleListeners.forEach((listener) => {
        listener(true)
    })
}

const ensureWindowResizeListener = () => {
    if (resizeListenerAttached || typeof window === "undefined") {
        return
    }
    resizeListenerAttached = true
    window.addEventListener("resize", () => {
        if (resizeSettleTimer != null) {
            clearTimeout(resizeSettleTimer)
        }
        resizeSettleTimer = setTimeout(() => {
            resizeSettleTimer = null
            notifyResizeSettled()
        }, RESIZE_SETTLE_MS)
    }, {passive: true})
}

/**
 * Defers and debounces ResizeObserver work to avoid layout feedback loops,
 * especially while the viewport width is changing.
 */
export const createResizeObserverScheduler = (callback, {debounceMs = RESIZE_OBSERVER_DEBOUNCE_MS} = {}) => {
    let debounceId = null
    let outerRafId = null
    let innerRafId = null

    const cancel = () => {
        if (debounceId != null) {
            clearTimeout(debounceId)
            debounceId = null
        }
        if (outerRafId != null) {
            cancelAnimationFrame(outerRafId)
            outerRafId = null
        }
        if (innerRafId != null) {
            cancelAnimationFrame(innerRafId)
            innerRafId = null
        }
    }

    const runCallback = () => {
        outerRafId = requestAnimationFrame(() => {
            outerRafId = null
            innerRafId = requestAnimationFrame(() => {
                innerRafId = null
                callback()
            })
        })
    }

    const schedule = (force = false) => {
        cancel()

        if (force) {
            runCallback()
            return
        }

        debounceId = setTimeout(() => {
            debounceId = null
            runCallback()
        }, debounceMs)
    }

    const onResizeSettled = (force) => schedule(force)
    ensureWindowResizeListener()
    resizeSettleListeners.add(onResizeSettled)

    schedule.cancel = () => {
        resizeSettleListeners.delete(onResizeSettled)
        cancel()
    }

    return schedule
}
